import { memo, useState } from 'react'
import { Input } from '@/components/ui/input'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { DatePicker } from '@/components/ui/date-picker'
import { TimePicker } from '@/components/ui/time-picker'
import Funnel from '@/components/funnel'
import { useCampaignName } from './hooks/useCampaignName'
import { getAvailableStages } from '@/utils/ad-campaigns-helpers'
import { useWizard } from '@/components/common/wizard/WizardContext'

const CampaignDetailsSection = memo(
    ({
        reviewLaunchData,
        updateReviewLaunchData,
        formatTimeLeft,
        isAddAudience = false,
        isEditAdSetMode = false,
    }) => {
        const { getStepContentData } = useWizard()
        const salesFunnelData = getStepContentData('salesFunnelSetup') || {}
        const [audienceSelections] = useState(salesFunnelData.audienceSelections || {})

        const selectFormatData = getStepContentData('campaignObjective') || {}
        const selectedObjective = selectFormatData.selectedObjective?.toLowerCase() ?? 'awareness'

        const availableStages = getAvailableStages(selectedObjective)

        // Auto-populate campaign name
        useCampaignName(reviewLaunchData, updateReviewLaunchData)

        return (
            <div className="tw:pt-6 tw:px-6 tw:border-b tw:border-gray-600">
                <div className="tw:flex tw:gap-8">
                    {/* Left side - Campaign details */}
                    <div className="tw:flex-1 tw:space-y-6">
                        {/* Campaign Name */}
                        <div className="tw:space-y-2">
                            <label className="tw:text-white tw:text-sm tw:font-semibold">
                                {isEditAdSetMode ? 'Ad campaign' : 'Campaign Name'}
                            </label>
                            <Input
                                value={reviewLaunchData.campaignName}
                                onChange={(e) =>
                                    !isAddAudience &&
                                    !isEditAdSetMode &&
                                    updateReviewLaunchData({ campaignName: e.target.value })
                                }
                                className="tw:w-full"
                                disabled={isAddAudience || isEditAdSetMode}
                            />
                        </div>

                        {/* Launch Date */}
                        {!isEditAdSetMode && (
                        <div className="tw:space-y-3">
                            <label className="tw:text-white tw:text-lg tw:font-semibold">When to Launch</label>
                            <RadioGroup
                                value={reviewLaunchData.launchOption}
                                onValueChange={(value) => updateReviewLaunchData({ launchOption: value })}
                            >
                                <div className="tw:flex tw:items-center">
                                    <RadioGroupItem value="launch_now" id="launch_now" />
                                    <label
                                        htmlFor="launch_now"
                                        className="tw:ml-2 tw:text-white tw:text-sm tw:cursor-pointer"
                                    >
                                        Launch now
                                    </label>
                                </div>
                                <div className="tw:flex tw:items-center">
                                    <RadioGroupItem value="launch_tonight" id="launch_tonight" />
                                    <label
                                        htmlFor="launch_tonight"
                                        className="tw:ml-2 tw:text-white tw:text-sm tw:cursor-pointer"
                                    >
                                        Launch at midnight
                                    </label>
                                </div>
                                <div className="tw:text-[#899CBD] tw:text-sm tw:ml-6">{formatTimeLeft()}</div>
                                <div className="tw:flex tw:items-center">
                                    <RadioGroupItem value="schedule" id="schedule" />
                                    <label
                                        htmlFor="schedule"
                                        className="tw:ml-2 tw:text-white tw:text-sm tw:cursor-pointer"
                                    >
                                        Schedule date and time
                                    </label>
                                </div>
                            </RadioGroup>

                            {/* Schedule date and time options */}
                            <div className="tw:space-y-3">
                                {reviewLaunchData.launchOption === 'schedule' && (
                                    <div className="tw:flex tw:gap-4 tw:ml-6">
                                        <DatePicker
                                            value={reviewLaunchData.launchDate}
                                            onChange={(date) => updateReviewLaunchData({ launchDate: date })}
                                            placeholder="07/10/2025"
                                            className="tw:w-[168px]"
                                        />
                                        <TimePicker
                                            value={reviewLaunchData.launchTime}
                                            onChange={(time) => updateReviewLaunchData({ launchTime: time })}
                                            placeholder="12:00 AM"
                                            className="tw:w-[168px]"
                                            selectedDate={reviewLaunchData.launchDate}
                                        />
                                    </div>
                                )}

                                {reviewLaunchData.launchOption === 'schedule' && (
                                    <div className="tw:text-[#899CBD] tw:text-sm tw:ml-6">
                                        {formatTimeLeft(reviewLaunchData.launchDate, reviewLaunchData.launchTime)}
                                    </div>
                                )}
                            </div>
                        </div>
                        )}
                    </div>

                    {/* Right side - Sales Funnel */}
                    <div className="tw:flex-none tw:border-l tw:p-10">
                        <div className="tw:space-y-2">
                            <div className="tw:flex tw:justify-center">
                                <Funnel availableStages={availableStages} audienceSelections={audienceSelections} />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        )
    },
)

CampaignDetailsSection.displayName = 'CampaignDetailsSection'

export default CampaignDetailsSection
